import {CircleAlert, Image as ImageIcon, Plus, RefreshCw, Trash2} from "lucide-react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import type {TaskSummary} from "@book-video-studio/contracts";
import {deleteTasks, listTasks, trashTasks} from "../api";
import {ConfirmDialog} from "../components/ConfirmDialog";
import {TaskStatus} from "../components/TaskStatus";

const formatTime = (value: string): string => new Intl.DateTimeFormat("zh-CN", {
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit"
}).format(new Date(value));

type PendingAction = {kind: "trash" | "delete"; taskIds: string[]};

export const TaskListPage = ({trashed = false}: {trashed?: boolean}) => {
  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setTasks(await listTasks(trashed));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "加载失败");
    } finally {
      setLoading(false);
    }
  }, [trashed]);
  useEffect(() => {
    setSelectedIds(new Set());
    void load();
  }, [load]);

  const selectedTaskIds = useMemo(
    () => tasks.filter((task) => selectedIds.has(task.id)).map((task) => task.id),
    [selectedIds, tasks]
  );
  const allSelected = tasks.length > 0 && selectedTaskIds.length === tasks.length;
  const toggleTask = (taskId: string): void => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };
  const toggleAll = (): void => {
    setSelectedIds(allSelected ? new Set() : new Set(tasks.map((task) => task.id)));
  };
  const confirmAction = async (): Promise<void> => {
    if (!pendingAction || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      if (pendingAction.kind === "trash") await trashTasks(pendingAction.taskIds);
      else await deleteTasks(pendingAction.taskIds);
      setPendingAction(null);
      setSelectedIds(new Set());
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "操作失败，请稍后重试");
      setPendingAction(null);
    } finally {
      setSubmitting(false);
    }
  };
  const actionKind = trashed ? "delete" : "trash";
  const actionLabel = trashed ? "彻底删除" : "移入回收站";
  const pendingCount = pendingAction?.taskIds.length ?? 0;

  return (
    <div className={`page-grid ${trashed ? "trash-page" : ""}`}>
      <aside className="process-rail">
        <span>{trashed ? "04" : "01"}</span><b>{trashed ? "回收站" : "任务队列"}</b>
        <p>{trashed ? "成片仍可预览下载；彻底删除后云端资源不可恢复。" : "每条任务拥有独立素材、执行尝试与本地上下文。"}</p>
      </aside>
      <section className="page-content">
        <header className="page-heading">
          <div><p className="eyebrow">{trashed ? "RECYCLE BIN" : "PRODUCTION QUEUE"}</p><h1>{trashed ? "回收站" : "任务"}</h1></div>
          <div className="heading-actions">
            <button className="icon-button" type="button" onClick={() => void load()} disabled={loading || submitting} aria-label="刷新"><RefreshCw size={19} /></button>
            {!trashed ? <Link className="button button-primary" to="/tasks/new"><Plus size={19} />新建任务</Link> : null}
          </div>
        </header>
        {error ? <p className="notice notice-error" role="alert"><CircleAlert size={19} />{error}</p> : null}
        {loading ? <p className="empty-state">正在读取任务……</p> : null}
        {!loading && tasks.length === 0 ? (
          <div className="empty-state">
            {trashed ? <Trash2 size={36} strokeWidth={1.4} /> : <ImageIcon size={36} strokeWidth={1.4} />}
            <h2>{trashed ? "回收站是空的" : "还没有任务"}</h2>
            <p>{trashed ? "移入回收站的任务会显示在这里。" : "上传一组图片和一段音频，制作第一条视频。"}</p>
            {!trashed ? <Link className="button button-primary" to="/tasks/new">新建任务</Link> : null}
          </div>
        ) : null}
        {!loading && tasks.length > 0 ? (
          <>
            <div className="batch-toolbar">
              <label className="selection-control">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="全选当前列表" />
                <span>全选</span>
              </label>
              <span>{selectedTaskIds.length > 0 ? `已选择 ${selectedTaskIds.length} 项` : `共 ${tasks.length} 项`}</span>
              <button
                className={`button ${trashed ? "button-danger" : "button-ghost"}`}
                type="button"
                disabled={selectedTaskIds.length === 0 || submitting}
                onClick={() => setPendingAction({kind: actionKind, taskIds: selectedTaskIds})}
              >
                <Trash2 size={17} />{actionLabel}
              </button>
            </div>
            <div className="task-list">
              {tasks.map((task, index) => (
                <article className={`task-row ${selectedIds.has(task.id) ? "is-selected" : ""}`} key={task.id}>
                  <label className="task-checkbox">
                    <input type="checkbox" checked={selectedIds.has(task.id)} onChange={() => toggleTask(task.id)} aria-label={`选择任务 ${task.title || task.id}`} />
                  </label>
                  <span className="task-number">{String(index + 1).padStart(2, "0")}</span>
                  <Link to={`/tasks/${task.id}`} className="task-main">
                    <b>{task.title || `图片轮播 ${task.id.slice(0, 8)}`}</b>
                    <small>{formatTime(trashed && task.trashedAt ? task.trashedAt : task.createdAt)} · {task.imageCount} 张图片{trashed ? " · 已回收" : ""}</small>
                  </Link>
                  <TaskStatus status={task.status} />
                  <span className="task-progress" aria-label={`进度 ${task.progress}%`}><i style={{width: `${task.progress}%`}} /></span>
                  <button className="row-action" type="button" disabled={submitting} onClick={() => setPendingAction({kind: actionKind, taskIds: [task.id]})} aria-label={`${actionLabel}：${task.title || task.id}`}>
                    <Trash2 size={18} />
                  </button>
                </article>
              ))}
            </div>
          </>
        ) : null}
      </section>
      <ConfirmDialog
        open={pendingAction !== null}
        title={pendingAction?.kind === "delete" ? `彻底删除 ${pendingCount} 个任务？` : `将 ${pendingCount} 个任务移入回收站？`}
        description={pendingAction?.kind === "delete"
          ? "任务素材和成片将从 COS 永久删除，数据库中的任务、事件和执行记录也会删除。此操作不可恢复。"
          : "任务将从任务列表隐藏，本地工作目录会由 Worker 清理；COS 素材和成片仍会保留。"}
        confirmLabel={pendingAction?.kind === "delete" ? "确认永久删除" : "确认移入回收站"}
        destructive={pendingAction?.kind === "delete"}
        busy={submitting}
        onCancel={() => setPendingAction(null)}
        onConfirm={() => void confirmAction()}
      />
    </div>
  );
};
